import { useState } from "react";
import { useLoggingContext } from "@/providers/logging-provider/logging-provider";
import { ErrorResponse } from "@/data/response.data";
import { useAnonServiceChecker } from "@/hooks/use-anon-service-checker";
import { keyBpmUsageStorage } from "@/services/storage/key-bpm-finder-session-storage";

export type FinderFile = {
  file: File;
  url: string;
  key?: string;
  bpm?: string;
  isLoading: boolean;
};

type FinderResponse = {
  key: string;
  bpm: string;
};

export const FINDER_FILES_ALLOWED = 10;

export const useKeyBpmFinder = () => {
  const [files, setFiles] = useState<FinderFile[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const logger = useLoggingContext();

  const { validate } = useAnonServiceChecker({
    serviceStorage: keyBpmUsageStorage,
    serviceName: "Key and BPM Finder",
    anonUserUsageCount: 2,
    authTrialUserUsageCount: 4,
    licenseValidator: (license) => license != "Trial",
  });

  const handleFilesAdded = async (newFiles: File[]) => {
    setIsLoading(true);
    if (!(await validate())) {
      setIsLoading(false);
      return;
    }

    newFiles = newFiles.slice(0, FINDER_FILES_ALLOWED);
    setFiles([
      ...files,
      ...newFiles.map((newFile) => ({
        file: newFile,
        url: URL.createObjectURL(newFile),
        key: undefined,
        bpm: undefined,
        isLoading: true,
      })),
    ]);

    try {
      await Promise.all(
        newFiles.map(async (file) => {
          const form = new FormData();
          form.append("file", file);

          const response = await fetch("/api/key-bpm-finder", {
            method: "POST",
            body: form,
          });

          if (response.ok) {
            const body = (await response.json()) as FinderResponse;
            setFileValues(file, body.key, body.bpm);
          } else {
            const body = (await response.json()) as ErrorResponse;
            setFileValues(file, "N/A", "N/A");
            logger.logError(new Error(body.message));
          }
        }),
      );
    } catch (err) {
      logger.logError(err as Error);
      setFiles((prev) => prev.map((file) => ({ ...file, isLoading: false })));
    } finally {
      setIsLoading(false);
    }
  };

  const setFileValues = (file: File, key: string, bpm: string) => {
    setFiles((prev) =>
      prev.map((detectorFile) => {
        if (detectorFile.file === file) {
          return {
            ...detectorFile,
            key: key,
            bpm: bpm,
            isLoading: false,
          };
        }

        return detectorFile;
      }),
    );
  };

  return { files, isLoading, handleFilesAdded };
};
